/* eslint-disable no-restricted-syntax */
/* eslint-disable no-continue */
import Vue from 'vue';
import Vuex from 'vuex';
import { gql } from '@apollo/client/core';
import ProductLegacyService from '@/services/ProductLegacyService';
import { getInitialState } from '../components/commons/ServerSideRendererUtils.js';
import { formatPrice } from '@/components/commons/MoneyUtils.js';
import { executeQuery, trackEvent } from '@/components/commons/utils.js';
import { trackProductClick } from '@/components/commons/AlgoliaAnalytics.js';
import Tracking from '../components/commons/Tracking.js';
import useCrossSell from '@/compositions/useCrossSell.js';

Vue.use(Vuex);

const PRODUCT_PAGE_QUERY = gql`
  query product($productHashKey: ID!) {
    product(hashKey: $productHashKey) {
      hashKey
      baseHashKey
      activeVersionHashKey
      alternativeHashKeys
      slug
      status
      active
      skuNo
      title
      imageUrl
      description
      marketingTitle
      marketingDescription
      metaTitle
      metaDescription
      fabricAndStain
      packagedItemQuantity
      inStock
      onSale
      onClearance
      configuratorEnabled
      collection {
        id
        name
        slug
        productCount
        productThumbnail {
          hashKey
          skuNo
          title
          slug
          imageUrl
          onSale
          onClearance
          price {
            currencyCode
            sale
            price
            retail
            clearance
          }
        }
      }
      review {
        count
        rating
      }
      taxonomyCategory {
        id
        name
        pixleeAlbumId
      }
      navigationCategory {
        id
        name
        displayName
        slug
        parentId
        parentName
        parentDisplayName
        parentSlug
      }
      price {
        currencyCode
        sale
        price
        retail
        clearance
      }
      care
      spec {
        key
        value
      }
      feature
      media {
        type
        url
        thumbnailUrl
        dashUrl
        hlsUrl
      }
      related {
        hashKey
        skuNo
        title
        slug
        imageUrl
        onSale
        onClearance
        price {
          currencyCode
          sale
          price
          retail
          clearance
        }
      }
      variation {
        hashKey
        fabricAndStain
        popularity
        variationImageUrl
        title
        slug
      }
      similarSku {
        hashKey
        skuNo
      }
      taxonomyAttribute {
        attributeId
        name
        type
        configuratorAttribute
        valueId
        value
        imageUrl
      }
      configurationGroup {
        attributeId
        name
        type
        configuration {
          hashKey
          valueId
          value
          imageUrl
          selected
        }
      }
      box {
        height
        width
        depth
      }
      height
      width
      depth
    }
  }
`;

async function load(id) {
  try {
    if (process.env.STOREFRONT) {
      const result = await executeQuery(PRODUCT_PAGE_QUERY, {
        productHashKey: id,
      });

      return JSON.parse(JSON.stringify(result));
    }

    const result = await ProductLegacyService.loadProduct(id);
    return result;
  } catch (error) {
    Tracking.sentryCaptureError(error);
    return {
      error: `Failed to load product with id ${id}`,
    };
  }
}

let state = {
  product: {
    hashKey: null,
    baseHashKey: null,
    activeVersionHashKey: null,
    alternativeHashKeys: [],
    skuNo: null,
    title: null,
    slug: null,
    active: null,
    status: null,
    imageUrl: null,
    description: null,
    marketingTitle: null,
    marketingDescription: null,
    metaTitle: null,
    metaDescription: null,
    fabricAndStain: null,
    packagedItemQuantity: null,
    inStock: false,
    onSale: false,
    onClearance: false,
    configuratorEnabled: false,
    collection: {
      id: null,
      name: null,
      slug: null,
      productCount: null,
      productThumbnail: [],
    },
    review: {
      rating: null,
      count: null,
    },
    taxonomyCategory: {},
    navigationCategory: [],
    price: [],
    care: [],
    spec: [],
    feature: [],
    media: [],
    related: [],
    variation: [],
    similarSku: [],
    taxonomyAttribute: [],
    configurationGroup: [],
    landingPageId: [],
    box: [],
  },
  error: null,
  loading: false,
  showUgc: true,
  inStockIn: null,
  currentOpenConfigurator: null,
  showHeadCart: false,
  hideTopInfo: false,
  carouselProducts: {},
  popularInCategory: [],
  crossSellProducts: [],
};

export const actions = {
  async load({ commit, dispatch }, { id }) {
    commit('startLoading');
    const result = await load(id);

    commit('updateProductState', result);
    await dispatch('loadCrossSellProducts');
    commit('finishLoading');
  },

  trackCartProducts({ state, getters }) {
    trackEvent('Products', 'Add To Cart', state.product.skuNo, getters.price.final / 100, {
      name: state.product.title,
      id: state.product.skuNo,
      price: getters.price.final / 100,
      currencyCode: getters.price.currency,
      category: getters.currentNavigationCategoryPath,
    });
  },

  trackProductView({ state, getters }) {
    try {
      trackEvent('Product', 'Product Viewed', `${state.product.skuNo} - ${state.product.title}`, getters.price.final / 100, {
        currencyCode: getters.price.currency,
        name: state.product.title,
        id: state.product.skuNo,
        price: getters.price.final / 100,
        category: getters.currentNavigationCategoryPath,
      });

      if ('ga' in window) {
        ga('set', 'page', window.location.pathname);
        ga('send', 'pageview');
      }

      trackProductClick(state.product.hashKey);
    } catch (e) {
      console.warn(e);
    }
  },

  async loadConfigurations({ state, getters, commit, dispatch }, payload) {
    if (!payload.value || !payload.configurationGroup) {
      return;
    }

    commit('updateSelectedConfigurations', payload);

    if (payload.value.hashKey) {
      commit('startLoading');
      const result = await load(payload.value.hashKey);
      commit('updateProductState', result);
      await dispatch('loadCrossSellProducts');
      commit('finishLoading');
      return;
    }

    commit('startLoading');
    const result = await ProductLegacyService.loadConfigurations({
      product: state.product,
      configurationChanged: payload.value,
    });
    commit('updateProductState', result);
    commit('finishLoading');
  },

  // TODO: move to a composition
  async loadPopularInCategory({ state, getters, commit }, filter) {
    if (!getters.currentNavigationCategory) {
      return;
    }

    const searchParams = {
      'filter.categories': getters.currentNavigationCategory.id,
      'filter.sort': 'POPULARITY',
    };
    const response = await ProductLegacyService.searchProducts(Object.assign(searchParams, filter));
    commit('updatePopularInCategory', state.popularInCategory.concat(response));
  },

  async loadCrossSellProducts({ commit, state }) {
    if (state?.product) {
      const { fetchCrossSellProducts } = useCrossSell();
      const products = await fetchCrossSellProducts([state.product.hashKey]);
      commit('updateCrossSellProducts', products);
    }
  },
};

export const mutations = {
  updateProductState(state, payload) {
    Object.assign(state, payload);
  },
  updateShowUgc(state, payload) {
    state.showUgc = payload;
  },
  updateSelectedConfigurations(state, payload) {
    const configurationGroup = state.product.configurationGroup.find((cg) => cg.attributeId === payload.configurationGroup.attributeId);
    if (configurationGroup) {
      configurationGroup.configuration.forEach((config) => (config.selected = config.valueId === payload.value.valueId));
    }
  },

  // TODO: Move into PLA page
  updateHeadCartVisibility(state, toggle) {
    state.showHeadCart = toggle;
  },

  // TODO: Move into PLA page
  updateTopInfo(state, toggle) {
    state.hideTopInfo = toggle;
  },

  // TODO: Move into PLA page
  updatePopularInCategory(state, list) {
    state.popularInCategory = list;
  },

  updateCurrentOpenConfigurator(state, id) {
    if (state.currentOpenConfigurator === id) {
      state.currentOpenConfigurator = null;
    } else {
      state.currentOpenConfigurator = id;
    }
  },

  startLoading: (state) => (state.loading = true),

  finishLoading: (state) => (state.loading = false),

  updateCrossSellProducts: (state, products) => (state.crossSellProducts = products),
};

export const getters = {
  lastImage(state) {
    return state.product && state.product.media ? [...state.product.media].reverse().find((media) => media.type === 'IMAGE' || !media.type) : null;
  },

  // TODO: Move into PLA page
  popularInCategory(state) {
    if (state.popularInCategory && state.popularInCategory.length) {
      return JSON.parse(JSON.stringify(state.popularInCategory));
    }
    return [];
  },

  configurations(state) {
    if (!state.product || !state.product.configurationGroup || !state.product.configurationGroup.length) {
      return [];
    }

    return state.product.configurationGroup.filter((group) => group.configuration.length > 1) || [];
  },

  configurationsLength: (state, getters) => (getters.configurations ? getters.configurations.length : 0),

  currentNavigationCategory: (state) => {
    return state.product?.navigationCategory?.[0] || {};
  },

  currentNavigationCategoryPath: (state, getters) => {
    const category = getters.currentNavigationCategory;
    if (category) {
      return [category.parentName, category.name].filter((name) => name).join('/');
    }
    return '';
  },

  reviewUrl: (state) => {
    if (state.product) {
      const url = encodeURIComponent(`/product/reviews?p=${state.product.skuNo}&productId=${state.product.hashKey}`);
      return `/login?next=${url}`;
    }

    return '';
  },

  productToCartFormat: (state) => [
    {
      id: state.product ? state.product.hashKey : null,
      quantity: state.product ? state.product.packagedItemQuantity : 1,
    },
  ],

  price: (state, getters, rootState, rootGetters) => {
    const product = state.product || {};
    const country = rootGetters['common/currentCountry'];

    const PAIR = 2;
    return formatPrice(product, country, state.forcePair && product.packagedItemQuantity === PAIR ? PAIR : 1);
  },
};

export default class ProductStore {
  static initialize({ init, forcePair }) {
    const data = init ? ProductLegacyService.upgradeLegacyState(getInitialState()) : {};

    state = {
      ...state,
      ...data,
      forcePair,
    };

    return {
      strict: process.env.DEBUG,
      namespaced: true,
      state,
      actions,
      mutations,
      getters,
    };
  }
}
